/**
 * PlayerCodeModal — renders an 8-char player link code in a top-drop
 * modal with a one-tap copy button. Used in two places:
 *   1. Teams page — coach taps the 🔗 icon on a player row to share.
 *   2. PlayerStats header — link-management modal lists existing codes
 *      using the same visual treatment.
 *
 * Pure presentational. Caller is responsible for fetching/computing the
 * code via GET /api/players/{player_id}/code.
 */
import { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Copy, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const NAVY = '#0B1824';
const ORANGE = '#ffa133';

export default function PlayerCodeModal({
  open,
  onOpenChange,
  code,
  playerName,
  teamName,
  loading = false,
}) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      toast.success('Code copied');
      setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error('Could not copy. Long-press the code to copy.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="player-code-modal">
        <DialogHeader>
          <DialogTitle
            className="text-lg font-bold uppercase tracking-tight"
            style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
          >
            Player Link Code
          </DialogTitle>
          <DialogDescription className="text-[13px]">
            Share this 8-character code with parents or other coaches so
            they can link <strong>{playerName || 'this player'}</strong>
            {teamName ? <> from <strong>{teamName}</strong></> : null} into
            their own player profile. The code is permanent and never
            changes.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <div
            className="rounded-2xl px-4 py-6 flex items-center justify-center select-all"
            style={{ background: NAVY, border: `2px solid ${ORANGE}` }}
            data-testid="player-code-value"
          >
            <span
              className="text-4xl tracking-[0.32em] font-black text-white"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              {loading ? '········' : (code || '········')}
            </span>
          </div>

          <Button
            type="button"
            onClick={onCopy}
            disabled={loading || !code}
            data-testid="player-code-copy"
            className="mt-4 w-full h-12 rounded-full font-bold uppercase tracking-[.1em]"
            style={{ background: copied ? '#16a34a' : NAVY, color: '#fff' }}
          >
            {copied ? (
              <><CheckCircle2 className="w-4 h-4 mr-2" /> Copied</>
            ) : (
              <><Copy className="w-4 h-4 mr-2" /> Copy Code</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
